import { useState, useRef } from 'react'
import './SyncStatusIndicator.css'
import { useSyncStatus, type EnrichedSyncQueueItem } from '../hooks/useSyncStatus'
import type { DeadLetterItem } from '../../db/client'
import { Modal, ModalHeader, ModalBody, ModalFooter } from './Modal'

function getEntityInfoFromDeadLetterItem(item: DeadLetterItem) {
  const cmd = item.command
  let type = 'update'
  let entity = 'scenario'
  let name = cmd.scenarioId
  
  if (cmd.commandType.startsWith('Create')) type = 'create'
  else if (cmd.commandType.startsWith('Delete')) type = 'delete'
  else if (cmd.commandType === 'CompleteItem') type = 'complete'
  
  if (cmd.commandType.includes('Item')) {
    entity = 'item'
    if (cmd.commandType === 'CreateItem') name = cmd.itemName
    else if (cmd.commandType === 'RenameItem') name = cmd.newName
    else if ('itemId' in cmd) name = cmd.itemId
  } else if (cmd.commandType.includes('List')) {
    entity = 'list'
    if (cmd.commandType === 'CreateList') name = cmd.listName
    else if (cmd.commandType === 'RenameList') name = cmd.newName
    else if ('listId' in cmd) name = cmd.listId
  } else if (cmd.commandType.includes('Scenario')) {
    entity = 'scenario'
    if (cmd.commandType === 'CreateScenario') name = cmd.name
    else if (cmd.commandType === 'RenameScenario') name = cmd.newName
  }
  
  return { type, entity, name, entityId: cmd.scenarioId }
}

export function SyncStatusIndicator() {
  const {
    pendingSyncCount,
    pendingSyncItems,
    syncLog,
    showSyncDetails,
    setShowSyncDetails,
    handleSyncBadgeClick,
    formatSyncItemDescription,
    syncStatus,
    failedItems,
    retryFailedItem,
    discardFailedItem,
    isRefreshingScenarios,
  } = useSyncStatus()

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const triggerRef = useRef<HTMLDivElement>(null)

  const toggleExpand = (id: string) => {
    const newSet = new Set(expandedItems)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setExpandedItems(newSet)
  }

  const handleCloseModal = () => {
    setShowSyncDetails(false)
    // Return focus to trigger element
    setTimeout(() => triggerRef.current?.focus(), 0)
  }

  const handleRetry = async (item: any) => {
    await retryFailedItem(item)
  }

  const handleDiscard = async (item: any) => {
    if (!confirm(`Permanently discard this operation? This cannot be undone.\n\n${item.failureReason}`)) {
      return
    }

    await discardFailedItem(item)
  }

  // Determine status display state
  let statusState = 'online';
  let statusTitle = 'Synced';
  let StatusContent = () => (
      <>
        <span className="dot"></span>
        <span className="label">Online</span>
      </>
  );

  // Priority 1: Errors (Blocking)
  if (failedItems.length > 0) {
    statusState = 'error';
    statusTitle = `${failedItems.length} failed operations`;
    StatusContent = () => (
      <>
        <span className="icon">⚠</span>
        <span className="label">{failedItems.length} Error{failedItems.length > 1 ? 's' : ''}</span>
      </>
    );
  }
  // Priority 2: Refreshing scenarios (Background sync)
  else if (isRefreshingScenarios) {
    statusState = 'syncing';
    statusTitle = 'Refreshing scenarios...';
    StatusContent = () => (
      <>
        <svg
          className="sync-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <polyline
            points="23 4 23 10 17 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points="1 20 1 14 7 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="label">Refreshing...</span>
      </>
    );
  }
  // Priority 3: Syncing/Pending (Active)
  else if (pendingSyncCount > 0) {
    if (syncStatus === 'online') {
      statusState = 'syncing';
      statusTitle = `${pendingSyncCount} items syncing`;
      StatusContent = () => (
        <>
          <svg
            className="sync-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <polyline
              points="23 4 23 10 17 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="1 20 1 14 7 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="label">Syncing {pendingSyncCount}...</span>
        </>
      );
    } else if (syncStatus === 'apiDown') {
      statusState = 'offline';
      statusTitle = `Server temporarily unreachable - ${pendingSyncCount} changes saved locally. Will retry automatically.`;
      StatusContent = () => (
        <>
          <span className="icon">☁</span>
          <span className="label">Queued ({pendingSyncCount})</span>
        </>
      );
    } else {
      statusState = 'offline';
      statusTitle = `Offline - ${pendingSyncCount} changes queued`;
      StatusContent = () => (
        <>
          <span className="icon">☁</span>
          <span className="label">Offline ({pendingSyncCount})</span>
        </>
      );
    }
  }
  // Priority 4: Connection Status (Passive)
  else if (syncStatus === 'offline') {
    statusState = 'offline';
    statusTitle = 'You are offline. Changes saved locally.';
    StatusContent = () => (
      <>
        <span className="icon">☁</span>
        <span className="label">Offline</span>
      </>
    );
  }
  else if (syncStatus === 'apiDown') {
    statusState = 'offline';
    statusTitle = 'Server temporarily unreachable. Changes saved locally.';
    StatusContent = () => (
      <>
        <span className="icon">☁</span>
        <span className="label">Reconnecting</span>
      </>
    );
  }

  return (
    <>
      <div 
        ref={triggerRef}
        className={`status-pill ${statusState}`}
        onClick={handleSyncBadgeClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleSyncBadgeClick()
          }
        }}
        title={statusTitle}
        role="button"
        tabIndex={0}
        aria-label={statusTitle}
        aria-expanded={showSyncDetails}
      >
        <StatusContent />
      </div>

      <Modal
        isOpen={showSyncDetails}
        onClose={handleCloseModal}
        size="large"
      >
        <ModalHeader
          title="Sync Status"
          onClose={handleCloseModal}
        />
        <ModalBody>
              <div className={`connection-status-banner ${statusState}`}>
                <div className="banner-icon">
                  <StatusContent />
                </div>
                <p className="banner-description">
                  {statusState === 'error'
                    ? "Some operations couldn't sync. Review and retry or discard them below."
                    : statusState === 'syncing'
                    ? 'Syncing your changes to the server…'
                    : syncStatus === 'offline'
                    ? "You're offline. Changes are saved on this device and will sync when you're back online."
                    : syncStatus === 'apiDown'
                    ? 'The server is temporarily unreachable. Your changes are safely saved on this device and will sync automatically.'
                    : 'All changes are synced to the server.'}
                </p>
              </div>

              {failedItems.length > 0 && (
                <div className="sync-section failed-section">
                  <h4>Failed Operations ({failedItems.length})</h4>
                  <div className="sync-items-list">
                    {failedItems.map((item) => {
                      const entityInfo = getEntityInfoFromDeadLetterItem(item)
                      const isExpanded = expandedItems.has(item.commandId)
                      return (
                        <div 
                          key={item.commandId} 
                          className={`sync-item compact failed ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleExpand(item.commandId)}
                        >
                          <div className="sync-item-header">
                            <span className="status-icon failed">⚠</span>
                            <span className="description">
                               {entityInfo.type.charAt(0).toUpperCase() + entityInfo.type.slice(1)} {entityInfo.entity}
                               {entityInfo.name && entityInfo.name !== entityInfo.entityId && `: ${entityInfo.name}`}
                            </span>
                            <span className="chevron">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                          
                          {isExpanded && (
                            <div className="sync-item-details">
                              <div className="detail-row">
                                <span className="label">Time:</span>
                                <span className="value">{new Date(item.failureTimestamp).toLocaleString()}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">ID:</span>
                                <span className="value mono">{item.commandId}</span>
                              </div>
                              <div className="sync-error">
                                <strong>Error:</strong> {item.failureReason}
                              </div>
                              <div className="sync-item-actions">
                                <button onClick={(e) => { e.stopPropagation(); handleRetry(item); }} className="retry-btn">
                                  Retry
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleDiscard(item); }} className="discard-btn">
                                  Discard
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              <div className="sync-section">
                <h4>Pending ({pendingSyncItems.length})</h4>
                {pendingSyncItems.length > 0 ? (
                  <div className="sync-items-list">
                    {pendingSyncItems.map((item, index) => {
                      const id = `pending-${index}` // Pending items might not have a stable ID in this view easily, using index or commandId if available
                      const description = formatSyncItemDescription(item as EnrichedSyncQueueItem)
                      const isExpanded = expandedItems.has(id)
                      
                      return (
                        <div 
                          key={id} 
                          className={`sync-item compact pending ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleExpand(id)}
                        >
                          <div className="sync-item-header">
                            <span className="status-icon pending">↻</span>
                            <span className="description">{description}</span>
                            <span className="chevron">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                          
                          {isExpanded && (
                            <div className="sync-item-details">
                              <div className="detail-row">
                                <span className="label">Time:</span>
                                <span className="value">{new Date(item.timestamp).toLocaleString()}</span>
                              </div>
                              {item.retries > 0 && (
                                <div className="detail-row">
                                  <span className="label">Retries:</span>
                                  <span className="value">{item.retries}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="no-items">No pending syncs</p>
                )}
              </div>

              <div className="sync-section">
                <h4>Recent History</h4>
                {syncLog.length > 0 ? (
                  <div className="sync-items-list">
                    {syncLog.filter(entry => !failedItems.some(f => {
                      const fInfo = getEntityInfoFromDeadLetterItem(f)
                      return fInfo.entityId === entry.entityId && fInfo.type === entry.type
                    })).map((entry) => {
                      const isExpanded = expandedItems.has(entry.id)
                      // Fallback for when entityName is just an ID (starts with "cmd_" or "item_" or just UUID)
                      // Ideally we'd fix the log entry, but for now we detect and hide if it looks like an ID
                      const looksLikeId = entry.entityName && (
                          entry.entityName.startsWith('cmd_') || 
                          entry.entityName.length > 20 && entry.entityName.includes('-')
                      );
                      const displayEntityName = looksLikeId ? '' : entry.entityName;

                      return (
                        <div 
                          key={entry.id} 
                          className={`sync-item compact ${entry.status} ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleExpand(entry.id)}
                        >
                          <div className="sync-item-header">
                            <span className={`status-icon ${entry.status}`}>
                              {entry.status === 'success' ? '✓' : entry.status === 'discarded' ? '✕' : '⚠'}
                            </span>
                            <span className="description">
                              {entry.actionDescription ? (
                                entry.actionDescription
                              ) : (
                                <>{entry.type.charAt(0).toUpperCase() + entry.type.slice(1)} {entry.entity}</>
                              )}
                              {displayEntityName && `: ${displayEntityName}`}
                              {entry.status === 'discarded' && <span className="discarded-note"> (Discarded)</span>}
                            </span>
                            <span className="chevron">{isExpanded ? '▲' : '▼'}</span>
                          </div>

                          {isExpanded && (
                            <div className="sync-item-details">
                              <div className="detail-row">
                                <span className="label">Time:</span>
                                <span className="value">{new Date(entry.timestamp).toLocaleString()}</span>
                              </div>
                              {entry.retries > 0 && (
                                <div className="detail-row">
                                  <span className="label">Retries:</span>
                                  <span className="value">{entry.retries}</span>
                                </div>
                              )}
                              {entry.error && (
                                <div className="sync-error">
                                  {entry.error}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="no-items">No sync history</p>
                )}
              </div>
        </ModalBody>
        <ModalFooter>
          <button onClick={handleCloseModal} className="cancel-btn">
            Close
          </button>
        </ModalFooter>
      </Modal>
    </>
  )
}
